import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from "@angular/material/dialog";
import {SizeScale, SizeScaleRequestDto} from "app/models/menu.model";
import {BaseModalComponent} from "app/shared/base-modal/base-modal.component";
import {FormControl, FormGroup} from "@angular/forms";
import {SizeScaleService} from "app/services/size-scale.service";
import {Organization} from "app/models/organization.model";
import {Store} from "@ngrx/store";
import {tap} from "rxjs/operators";
import {concatLatestFrom} from "@ngrx/effects";
import {selectSizeScales} from "app/reducers/menu.selectors";
import {setSizeScales} from "app/reducers/menu.actions";
import {
  DELETE_SIZE_SCALE,
  DeleteSizeScaleDialogComponent, DeleteSizeScaleDialogData
} from "app/pages/menu/shared/delete-size-scale-dialog/delete-size-scale-dialog.component";

@Component({
  selector: 'app-edit-size-scale-modal',
  templateUrl: './edit-size-scale-modal.component.html',
  styleUrls: ['./edit-size-scale-modal.component.scss']
})
export class EditSizeScaleModalComponent extends BaseModalComponent {
  form: FormGroup;
  initValues: SizeScale;

  visualTypes = [
    'Default',
    'Minimal',
  ];

  constructor(
    private sizeScaleService: SizeScaleService,
    private store: Store,
    private dialog: MatDialog,
    public dialogRef: MatDialogRef<EditSizeScaleModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditSizeScaleModalComponentDataType
  ) {
    super(dialogRef);
    this.initValues = {...data.sizeScale}

    this.form = new FormGroup({});

    this.form.addControl('sizeScaleName', new FormControl(data.sizeScale.name))
    this.form.addControl('visualType', new FormControl(data.sizeScale.visualType))

    if (this.data.organization.additionalLanguages) {
      for (const lang of this.data.organization.additionalLanguages) {
        this.form.addControl('sizeScaleName'+lang, new FormControl(data.sizeScale.nameTranslations[lang]));
      }
    }
  }

  submit() {
    let hasChanges = this.hasChanges();
    const sizeScaleRequestDto : SizeScaleRequestDto = {
      name: this.form.value['sizeScaleName'],
      visualType: this.form.value['visualType'],
      nameTranslations: {},
      organizationId: this.data.organization.id,
    }

    if (this.data.organization.additionalLanguages) {
      for (const lang of this.data.organization.additionalLanguages) {
        sizeScaleRequestDto.nameTranslations[lang] = this.form.value['sizeScaleName'+lang];
      }
    }

    if(!hasChanges){
      // No save if hasn't changes. Because every SizeScale update is update of the menu.
      this.exit(sizeScaleRequestDto)
      return;
    }

    if(this.data.sizeScaleId){
      this.sizeScaleService.updateSizeScale(this.data.sizeScaleId, sizeScaleRequestDto)
        .pipe(
          concatLatestFrom(() => this.store.select(selectSizeScales)),
          tap(([response, sizeScales]) => {
            const updatedSizeScale = sizeScales.map(ss => {
              if (ss.id === response.id) {
                return response;
              }
              return ss;
            });
            this.store.dispatch(setSizeScales(updatedSizeScale));
          }),
        ).subscribe((res) => this.exit(res));
    } else {
      this.sizeScaleService.createSizeScale(sizeScaleRequestDto)
        .pipe(
          concatLatestFrom(() => this.store.select(selectSizeScales)),
          tap(([response, sizeScales]) => {
            const updatedSizeScale = [...sizeScales, response];
            this.store.dispatch(setSizeScales(updatedSizeScale));
          }),
        ).subscribe((res) => this.exit(res));
    }
  }

  hasChanges(): boolean {
    if(this.form.value['sizeScaleName'] != this.data.sizeScale.name){
      return true;
    }

    if(this.form.value['visualType'] != this.data.sizeScale.visualType){
      return true;
    }

    if (this.data.organization.additionalLanguages) {
      for (const lang of this.data.organization.additionalLanguages) {
        if(this.data.sizeScale.nameTranslations[lang] != this.form.value['sizeScaleName'+lang]){
          return true;
        }
      }
    }

    return false;
  }

  remove(){
    if(!this.data.sizeScaleId) {
      return;
    }

    this.openDeleteSizeScaleDialog();
  }

  openDeleteSizeScaleDialog(){
    const sizeScaleId = this.data.sizeScaleId;
    this.sizeScaleService.canDeleteSizeScale(this.data.menuSectionId, sizeScaleId).subscribe(result => {
      this.dialog.open(DeleteSizeScaleDialogComponent, {
        data: {
          usingScalesInProducts: result
        } as DeleteSizeScaleDialogData
      }).afterClosed().subscribe(result => {
        if(result == DELETE_SIZE_SCALE) {
          this.exit(DELETE_SIZE_SCALE);
        }
      })
    });
  }
}

export interface EditSizeScaleModalComponentDataType {
  sizeScaleId: string | null,
  sizeScale: SizeScale,
  organization: Organization,
  menuSectionId: string;
}
